/**
 * Offline authentication (D5, D14, D20; PLAN §11): what a node needs to let a
 * working operator keep working while the internet is gone, and the
 * break-glass ways in when nobody is signed in yet.
 *
 *   1. THE DIRECTORY, synced cloud → node over the site link
 *      (`directory.snapshot` / `directory.delta`, `siteLink.ts`): the site's
 *      tenant users, their groups (permissions + scope), memberships, the
 *      Argon2id hashes of their offline PINs and of the site's emergency
 *      password, and the Cognito JWKS. Revocation is EVENTUAL: a change in
 *      the cloud reaches a connected node as a delta within seconds, and a
 *      node that missed one heals on its next snapshot (on every reconnect,
 *      and every `DIRECTORY_RESYNC_SECONDS` while connected).
 *   2. COGNITO TOKENS OFFLINE: a node verifies a Cognito ID/access token
 *      against the synced JWKS until the token expires; the grants come from
 *      the synced directory, never from the token.
 *   3. LOCAL SESSIONS: a node exchanges a valid Cognito token for a local
 *      session token — an EdDSA JWT signed with the SITE KEY that main and
 *      backup share, 12 h, sliding, bound to user + tenant + site — so both
 *      nodes accept it and an internet outage mid-show never signs anyone
 *      out. Every request re-checks the user against the synced directory.
 *   4. BREAK-GLASS, only while the node's cloud link is DOWN
 *      (`CLOUD_LINK_DOWN_AFTER_MS`): an offline PIN (users holding
 *      `local.login`) or the site's emergency account. Rate-limited, locked
 *      out after repeated failures, and every attempt audited on the node and
 *      reported to the cloud (`directory.audit`) once the link is back.
 *
 * The node-local routes (`POST /local/login`, `POST /local/session`,
 * `GET /local/auth`) are not in the route table: like `/health` and
 * `/dev/token`, they answer before a caller has a node credential, and only a
 * node serves them. Their shapes are here so the node, the CLI and the SPA
 * share them.
 */
import { z } from 'zod';
import type { PermissionGrant } from '@acp/domain/access.js';
import { emailSchema, entityIdSchema, grantScopeSchema, tenantIdSchema, userIdSchema } from './tenancy.js';

// ─── timings (each one cited where it is used) ───

/** A local session lives this long after its last use (D20: "12 h, sliding"). */
export const LOCAL_SESSION_TTL_SECONDS = 12 * 60 * 60;

/**
 * A request made with a local session older than this is answered with a
 * renewed one (`LOCAL_SESSION_HEADER`), so "sliding" costs one signature per
 * five minutes of activity rather than one per request.
 */
export const LOCAL_SESSION_RENEW_AFTER_SECONDS = 5 * 60;

/** The response header a node puts a new or renewed local session token in. */
export const LOCAL_SESSION_HEADER = 'x-acp-session';

/**
 * THE "LINK DOWN" PREDICATE. A node's cloud link is down when it has had no
 * successful site-link exchange — no valid frame received from the cloud —
 * for this long: three missed 30 s heartbeats (`SITE_LINK_STALE_MS`, the same
 * threshold at which the cloud calls the node's link stale). A node whose
 * link is configured but has never exchanged a frame since it started is
 * down too. Break-glass sign-in is accepted only while it holds.
 */
export const CLOUD_LINK_DOWN_AFTER_MS = 90_000;

/** What a node knows about its site link: whether one is configured, and when a frame last arrived from the cloud (wall-clock ms). */
export type CloudLinkRecord = { readonly configuredAtMs: number | null; readonly lastExchangeMs: number | null };

export type CloudLinkState = 'not-configured' | 'connected' | 'disconnected';

/**
 * The predicate, as a function: `not-configured` when no site link was ever
 * set up on this node (development, a node with no cloud); `connected` when
 * the last exchange is at most `CLOUD_LINK_DOWN_AFTER_MS` old; `disconnected`
 * otherwise — including never since it was configured, and an exchange
 * stamped more than `CLOCK_STEP_TOLERANCE_MS` in the future (the wall clock
 * stepped back: a link that is really up re-stamps within one heartbeat, so
 * this corrects itself, and it never leaves break-glass shut on a node that
 * is cut off).
 */
export function cloudLinkStateAt(link: CloudLinkRecord, nowMs: number): CloudLinkState {
  if (link.configuredAtMs === null && link.lastExchangeMs === null) return 'not-configured';
  if (link.lastExchangeMs === null) return 'disconnected';
  const age = nowMs - link.lastExchangeMs;
  return age <= CLOUD_LINK_DOWN_AFTER_MS && age >= -CLOCK_STEP_TOLERANCE_MS ? 'connected' : 'disconnected';
}

/** How far in the future a last-exchange stamp may be before it is distrusted (clock jitter between the node's processes). */
export const CLOCK_STEP_TOLERANCE_MS = 5_000;

/** A connected node asks for a fresh snapshot this often, healing any delta it missed. */
export const DIRECTORY_RESYNC_SECONDS = 10 * 60;

/** Break-glass: failures per account before a lockout, and how long it lasts. */
export const LOCAL_LOGIN_MAX_FAILURES = 5;
export const LOCAL_LOGIN_LOCKOUT_SECONDS = 15 * 60;
/** Break-glass: attempts one source address may make per minute, whatever the account. */
export const LOCAL_LOGIN_ATTEMPTS_PER_MINUTE = 20;

// ─── secrets ───

/** An offline PIN or password: at least six characters (the hash leaves the cloud: longer is stronger). */
export const offlinePinSchema = z.string().min(6, 'an offline PIN has at least 6 characters').max(128);

/** The account name the emergency account signs in with. */
export const EMERGENCY_ACCOUNT = 'emergency' as const;

/** The user id a site's emergency account acts as (in audit entries and its sessions). */
export function emergencyUserId(siteId: string): string {
  return `emergency:${siteId}`;
}

/**
 * What the site emergency account may do, on its own site only: run the
 * show — read and edit rundowns, take, activate, switch over. Not configure
 * devices, not administer anything.
 */
export function emergencyGrants(siteId: string): PermissionGrant[] {
  return [{ permissions: ['rundown.read', 'rundown.edit', 'playout.take', 'playout.activate', 'node.switchover'], scope: { kind: 'resources', siteIds: [siteId], studioIds: [] } }];
}

// ─── the directory on the wire (site link; payloads strip unknown fields, siteLink.ts) ───

const isoSchema = z.iso.datetime({ offset: true });

/** An Argon2id PHC string (`@acp/cloud-link/argon2.js`); its parameters are re-checked where it is verified. */
export const secretHashSchema = z.string().regex(/^\$argon2id\$/).max(256);

/**
 * A group as a node holds it. Permission ids are plain strings on the wire: a
 * newer cloud may send an id this node does not know, which it drops on read
 * rather than refusing the whole snapshot (PLAN §6.8).
 */
export const directoryGroupSchema = z.object({
  groupId: entityIdSchema,
  name: z.string().max(200),
  permissions: z.array(z.string().max(64)).max(64),
  scope: grantScopeSchema,
});
export type DirectoryGroup = z.infer<typeof directoryGroupSchema>;

/** A user as a node holds it, with their groups and the hash of their offline PIN (null: none set). */
export const directoryUserSchema = z.object({
  userId: userIdSchema,
  email: emailSchema,
  displayName: z.string().max(200),
  /** `disabled`: known, and refused. Today every user the cloud has is `active`; a deleted user is absent. */
  status: z.enum(['active', 'disabled']),
  groupIds: z.array(entityIdSchema).max(200),
  offlinePin: z.object({ hash: secretHashSchema, updatedAt: isoSchema }).nullable(),
});
export type DirectoryUser = z.infer<typeof directoryUserSchema>;

/** The site's emergency account: its password hash and when it was last rotated (a rotation ends its sessions). */
export const emergencyAccountSchema = z.object({ passwordHash: secretHashSchema, rotatedAt: isoSchema });
export type EmergencyAccount = z.infer<typeof emergencyAccountSchema>;

/** A JWK as JWKS carries it: the fields RSA and EC signing keys use, the rest kept. */
export const jwkSchema = z
  .object({ kty: z.string(), kid: z.string().max(256).optional(), alg: z.string().optional(), use: z.string().optional() })
  .loose();

/** The Cognito pool's key set, as the cloud fetched it. */
export const directoryJwksSchema = z.object({
  issuer: z.url(),
  keys: z.array(jwkSchema).max(20),
  fetchedAt: isoSchema,
});
export type DirectoryJwks = z.infer<typeof directoryJwksSchema>;

// ─── audit ───

export const offlineAuditKindSchema = z.enum(['offline-pin', 'emergency']);
export const offlineAuditOutcomeSchema = z.enum(['success', 'failure', 'locked', 'refused-online', 'rate-limited']);

/** One break-glass attempt, as the node recorded it and reports it (`directory.audit`). */
export const offlineAuditEventSchema = z.object({
  /** A ULID the node minted: the cloud stores an id once, so a re-sent batch is idempotent. */
  id: z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/),
  at: isoSchema,
  kind: offlineAuditKindSchema,
  outcome: offlineAuditOutcomeSchema,
  /** What was typed as the account: an email, or `emergency`. Never the secret. */
  account: z.string().max(254),
  /** The user it resolved to, or null (unknown account, emergency). */
  userId: z.string().max(128).nullable(),
  sourceIp: z.string().max(64).nullable(),
  nodeId: z.string().max(64),
});
export type OfflineAuditEvent = z.infer<typeof offlineAuditEventSchema>;

/** A tenant audit entry as the cloud stores and lists it. */
export const auditEntrySchema = z
  .object({
    auditId: z.string(),
    tenantId: tenantIdSchema,
    siteId: z.string(),
    at: isoSchema,
    receivedAt: isoSchema,
    kind: offlineAuditKindSchema,
    outcome: offlineAuditOutcomeSchema,
    account: z.string(),
    userId: z.string().nullable(),
    sourceIp: z.string().nullable(),
    nodeId: z.string(),
  })
  .strict();
export type AuditEntry = z.infer<typeof auditEntrySchema>;

// ─── cloud REST ───

export const setOfflinePinRequestSchema = z.object({ pin: offlinePinSchema }).strict();
export const offlinePinResponseSchema = z.object({ set: z.boolean(), updatedAt: isoSchema.nullable() }).strict();

export const emergencyAccountRequestSchema = z
  .object({
    /** True: replace an existing account's password (its sessions end on every node at the next sync). False: create it — an existing account is `conflict`. */
    rotate: z.boolean(),
  })
  .strict();

/** The emergency account's password is in this response ONCE: the cloud keeps only its hash. */
export const emergencyAccountResponseSchema = z
  .object({ siteId: entityIdSchema, account: z.literal(EMERGENCY_ACCOUNT), password: z.string(), rotatedAt: isoSchema, rotated: z.boolean() })
  .strict();

/** `GET /sites/{siteId}/emergency-account`: whether the site has one, and since when — never its password. */
export const emergencyAccountStatusSchema = z.object({ siteId: entityIdSchema, exists: z.boolean(), rotatedAt: isoSchema.nullable() }).strict();
export type EmergencyAccountStatus = z.infer<typeof emergencyAccountStatusSchema>;

export const listAuditQuerySchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(200).optional().describe('At most this many entries (default 50).'),
    before: z.string().max(200).optional().describe('The `next` cursor of a previous page.'),
  })
  .strict();
export const listAuditResponseSchema = z.object({ entries: z.array(auditEntrySchema), next: z.string().nullable() }).strict();

// ─── node-local REST ───

/** `POST /local/login`: break-glass. `account` is the user's email, or `emergency`. */
export const localLoginRequestSchema = z
  .object({ account: z.union([emailSchema, z.literal(EMERGENCY_ACCOUNT)]), secret: z.string().min(1).max(128) })
  .strict();
export type LocalLoginRequest = z.infer<typeof localLoginRequestSchema>;

export const localSessionKindSchema = z.enum(['cognito', 'offline-pin', 'emergency', 'dev']);
export type LocalSessionKind = z.infer<typeof localSessionKindSchema>;

/** A new local session (`POST /local/login`, `POST /local/session`). */
export const localSessionResponseSchema = z
  .object({
    token: z.string(),
    expiresAt: isoSchema,
    kind: localSessionKindSchema,
    userId: z.string(),
    email: z.string().nullable(),
    tenantId: z.string(),
    siteId: z.string(),
  })
  .strict();
export type LocalSessionResponse = z.infer<typeof localSessionResponseSchema>;

/** `GET /local/auth` (unauthenticated): what sign-in this node offers right now. */
export const localAuthStatusSchema = z
  .object({
    cloudLink: z.enum(['not-configured', 'connected', 'disconnected']),
    /** True exactly while the link is down (`CLOUD_LINK_DOWN_AFTER_MS`) — the only time `/local/login` is accepted. */
    offlineLogin: z.boolean(),
    /** Whether this node holds a site key, so it can issue local sessions at all. */
    localSessions: z.boolean(),
    /** When the node last heard from the cloud, or null. */
    lastCloudContact: isoSchema.nullable(),
    siteId: z.string(),
  })
  .strict();
export type LocalAuthStatus = z.infer<typeof localAuthStatusSchema>;
